import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { EnvelopeSimple, X } from "@phosphor-icons/react";

/**
 * v3.2.40 · Persistent yellow banner nudging unverified users to
 * confirm their email. Not dismissible — verification IS the dismissal.
 * Rendered in App layout above the Topbar.
 *
 * v3.2.56 · Auto-refresh on tab focus / visibility. When the user
 * clicks the verify link in their email, that opens a new tab which
 * consumes the token server-side. The ORIGINAL tab still holds a
 * cached `user.email_verified=false` and would keep the banner up
 * until manual refresh. We now re-hit `/auth/me` whenever this tab
 * becomes visible again, so the banner disappears the moment focus
 * returns from the mail app.
 */
export default function EmailVerifyBanner() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const unverified = user && !user.email_verified;

  useEffect(() => {
    if (!unverified) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [unverified, refresh]);

  if (!unverified) return null;

  const resend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/resend-verify");
      if (data.already_verified) {
        toast.success("Already verified.");
        refresh();
      } else if (data.ok) {
        setSent(true);
        toast.success("Verification email sent — check your inbox.");
      } else {
        toast.info("Verification email service is temporarily unavailable. Try again in a moment.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border-b border-[#FFCC00]/40 bg-[#FFCC00]/10 py-2 px-4"
         data-testid="email-verify-banner">
      <div className="max-w-6xl mx-auto flex items-center gap-3 flex-wrap">
        <EnvelopeSimple size={16} weight="duotone" className="text-[#FFCC00] shrink-0" />
        <div className="flex-1 min-w-0 text-xs font-mono text-[#FFCC00]">
          <span className="uppercase tracking-widest">// verify email</span>
          <span className="ml-2 text-gray-200">
            Check <span className="text-white font-bold">{user.email}</span> for a verification link.
            Some actions (posting jobs, submitting proposals, payments) require verification.
          </span>
        </div>
        <button onClick={resend}
          disabled={busy || sent}
          className="text-xs font-mono uppercase tracking-wider text-[#FFCC00] hover:text-white disabled:opacity-40 whitespace-nowrap"
          data-testid="email-verify-resend-btn">
          {sent ? "sent ✓" : busy ? "sending…" : "resend"}
        </button>
      </div>
    </div>
  );
}
